import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, ProgressBar, Table, CopyButton } from '../../../components';

// Default criteria structure for when data is not available
const defaultCriteria = {
  status: 'info',
  iconType: 'info',
  importance: { level: 0, text: '' },
  solvability: { level: 0, text: '' },
  isEmpty: true,
  message: null,
  data: null,
};

/**
 * Helper to determine progress bar color based on status
 * @param {string} status - The status ('success', 'warning', 'error', 'info')
 * @returns {string} - The hex color code
 */
const getProgressBarColor = (status) => {
  switch (status) {
    case 'success':
      return '#28a745';
    case 'warning':
      return '#EDBC0C';
    case 'error':
      return '#dc3545';
    default:
      return '#207DE9';
  }
};

/**
 * Helper to get message text based on percentage
 * @param {number} percentage - The mobile friendliness percentage
 * @returns {string} - The message text
 */
const getMobileFriendlinessMessage = (percentage) => {
  if (percentage >= 80) {
    return 'Mobile friendliness is good.';
  } else if (percentage >= 50) {
    return 'Mobile friendliness could be improved.';
  }
  return 'This web page is not optimized for Mobile Visitors.';
};

export const MobileSection = ({ id, siteUrl, data }) => {
  const [mobileFriendlinessExpanded, setMobileFriendlinessExpanded] = useState(false);
  const [tapTargetsExpanded, setTapTargetsExpanded] = useState(false);
  const [mobileViewportExpanded, setMobileViewportExpanded] = useState(false);

  // Extract data from props with defaults
  const mobileFriendliness = data?.mobileFriendliness || defaultCriteria;
  const tapTargets = data?.tapTargets || defaultCriteria;
  const mobileViewport = data?.mobileViewport || defaultCriteria;
  const mobileRendering = data?.mobileRendering || defaultCriteria;

  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  // Build tap targets table data
  const tapTargetsTableData = tapTargets?.data?.tapTargetsTable?.length > 0
    ? [
        ['Tap Target', 'Size', 'Overlapping Target'],
        ...tapTargets.data.tapTargetsTable.map((row) => [
          row?.tapTarget || '-',
          row?.size || '-',
          row?.overlappingTarget || '-',
        ]),
      ]
    : [];

  // Mobile friendliness percentage and message
  const percentage = mobileFriendliness?.data?.percentage || 0;
  const mobileFriendlinessMessage = mobileFriendliness?.isEmpty
    ? mobileFriendliness?.message || 'This web page is not optimized for Mobile Visitors.'
    : getMobileFriendlinessMessage(percentage);

  return (
    <ResultsItem 
      id={id}
      title="Mobile" 
      tooltip="Mobile is a big part of SEO. Search engines like Google use mobile-friendliness as a ranking factor for search results, which is why businesses can't wait when it comes to building a mobile site. Learn where your site stands when it comes to mobile-friendliness by checking out your results now!"
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="Mobile Friendliness"
        tooltip="Mobile-friendliness describes whether people can use and interact with your site on a mobile device, and how well they use and interact with your site on a mobile device."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={mobileFriendliness?.iconType || 'info'}
                showTooltip={false} 
              />
            }
          >
            {!mobileFriendliness?.isEmpty && mobileFriendliness?.data ? (
              <>
                <p className="text-primary">{mobileFriendlinessMessage}</p>
                <div className="score-row">
                  <ProgressBar
                    percentage={percentage}
                    color={getProgressBarColor(mobileFriendliness?.status)}
                  />
                  <span className="score-text">
                    <span className="text-strong">{percentage}</span>/100
                  </span>
                </div>
              </>
            ) : (
              <p className="text-primary">{mobileFriendliness?.message || 'This web page is not optimized for Mobile Visitors.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={mobileFriendlinessExpanded}
            onClick={() => setMobileFriendlinessExpanded(!mobileFriendlinessExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={mobileFriendlinessExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Mobile-Friendliness Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Have a responsive design versus a separate mobile site</li>
                <li>Limit the use of plugins like Flash, Java, and Silverlight</li>
                <li>Use AMP to improve the speed of pages on mobile devices</li>
              </ul>
            </IconContent>
          }
          sideContent={
            mobileFriendliness?.importance?.level > 0 || mobileFriendliness?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {mobileFriendliness?.importance?.level > 0 && (
                  <PriorityIndicator text={mobileFriendliness?.importance?.text || ''} level={mobileFriendliness?.importance?.level || 0} />
                )}
                {mobileFriendliness?.solvability?.level > 0 && (
                  <PriorityIndicator text={mobileFriendliness?.solvability?.text || ''} level={mobileFriendliness?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/seo/learn/mobile-seo/" target="_blank" rel="noopener noreferrer">
                    Mobile SEO: 9 Ways to Optimize for Mobile-friendly SEO
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/marketing/4-tips-for-creating-mobile-content/" target="_blank" rel="noopener noreferrer">
                    4 Tips for Creating Content for Mobile Users
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/how-to-use-google-amp-for-your-mobile-site/" target="_blank" rel="noopener noreferrer">
                    How to Use Google AMP for Your Site
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Tap Targets"
        tooltip="Your tap targets are the buttons on your site that users can click while on their mobile device. Tap targets need appropriately spaced to deliver the best user experience."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={tapTargets?.iconType || 'info'}
                showTooltip={false} 
              />
            }
          >
            {tapTargets?.hasFailingTargets && tapTargetsTableData.length > 0 ? (
              <div className="table-content-wrapper">
                <Table
                  data={tapTargetsTableData}
                  className="table-tap-targets"
                  id="tap-targets-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#tap-targets-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">
                {tapTargets?.message || "Perfect, your page's tap targets are big enough and have enough space between them."}
              </p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={tapTargetsExpanded}
            onClick={() => setTapTargetsExpanded(!tapTargetsExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={tapTargetsExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Tap Targets Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Increase your navigation menu&apos;s padding by 5-10 pixels</li>
                <li>Make your company logo a link to your website&apos;s homepage</li>
                <li>Try heatmaps to learn how users interact with your site and where they encounter issues</li>
              </ul>
            </IconContent>
          }
          sideContent={
            tapTargets?.importance?.level > 0 || tapTargets?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {tapTargets?.importance?.level > 0 && (
                  <PriorityIndicator text={tapTargets?.importance?.text || ''} level={tapTargets?.importance?.level || 0} />
                )}
                {tapTargets?.solvability?.level > 0 && (
                  <PriorityIndicator text={tapTargets?.solvability?.text || ''} level={tapTargets?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/web-design/responsive-menus/" target="_blank" rel="noopener noreferrer">
                    8 Ways to Add a Responsive Navigation Menu on Your Site
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/marketing/what-is-a-heatmap/" target="_blank" rel="noopener noreferrer">
                    What Is a Heatmap? [+3 Best Times to Use a Heatmap]
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Mobile Viewport"
        tooltip="Mobile viewport describes how content appears on different devices."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={mobileViewport?.iconType || 'info'}
                showTooltip={false} 
              />
            }
          >
            {!mobileViewport?.isEmpty && mobileViewport?.data ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p className="text-primary">
                  {mobileViewport?.data?.viewportStatus?.message || 'No viewport was detected'}
                </p>
                <p className="text-primary">
                  {mobileViewport?.data?.contentStatus?.message || ''}
                </p>
              </div>
            ) : (
              <p className="text-primary">{mobileViewport?.message || 'No viewport was detected'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={mobileViewportExpanded}
            onClick={() => setMobileViewportExpanded(!mobileViewportExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={mobileViewportExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Mobile Viewport Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Use the meta viewport tag in the head tag</li>
                <li>Skip using fixed-width elements, like large images</li>
                <li>Use relative, versus absolute, width values in your CSS</li>
              </ul>
            </IconContent>
          }
          sideContent={
            mobileViewport?.importance?.level > 0 || mobileViewport?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {mobileViewport?.importance?.level > 0 && (
                  <PriorityIndicator text={mobileViewport?.importance?.text || ''} level={mobileViewport?.importance?.level || 0} />
                )}
                {mobileViewport?.solvability?.level > 0 && (
                  <PriorityIndicator text={mobileViewport?.solvability?.text || ''} level={mobileViewport?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/web-design/a-comprehensive-guide-inside-your/" target="_blank" rel="noopener noreferrer">
                    Everything You Need to Know About The Head Tag
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/web-design/learn/redesign-website-for-mobile/" target="_blank" rel="noopener noreferrer">
                    How to Redesign Your Website for Mobile
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Mobile Rendering"
        tooltip="What your website looks like on mobile devices."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={mobileRendering?.iconType || 'info'}
                showTooltip={false} 
              />
            }
          >
            {!mobileRendering?.isEmpty && mobileRendering?.data?.phone ? (
              <div className="mobile-screenshot">
                <div className="mobile-screenshot__screen">
                  <img 
                    src={mobileRendering?.data?.phone}
                    alt="Mobile rendering preview"
                  />
                </div>
                <div className="mobile-screenshot__device"></div>
                <div className="mobile-screenshot__notch"></div>
              </div>
            ) : (
              <p className="text-primary">
                {mobileRendering?.message || 'The Mobile Rendering was not able to be generated.'}
              </p>
            )}
          </IconContent>
        }
      />
    </ResultsItem>
  );
};
